import React, { useState } from 'react';
import { ContactFormData } from '../types';
import { parseNumber, formatInputNumber } from '../utils/mortgageCalculations';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredContact: 'email',
    mortgageAmount: undefined,
    propertyValue: undefined
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Naam is verplicht';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Voer een geldig e-mailadres in';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefoonnummer is verplicht';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Bericht is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ContactFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNumberInputChange = (field: 'mortgageAmount' | 'propertyValue', event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputNumber(event.target.value);
    event.target.value = formatted;
    const numValue = parseNumber(formatted);
    handleInputChange(field, numValue || undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== true) {
        throw new Error(
          data.message || data.error || 'Er is een fout opgetreden bij het verzenden'
        );
      }

      setSubmissionMessage(data.message || 'Uw bericht is door de mailserver geaccepteerd.');
      setIsSubmitted(true);
    } catch (error) {
      setErrors({
        submit: error instanceof Error
          ? error.message
          : 'Er is een fout opgetreden. Probeer het later opnieuw.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="fade-in">
        <div className="card text-center">
          <h1>Bedankt voor uw bericht!</h1>
          <div className="alert alert-success">
            <strong>✅ {submissionMessage}</strong>
            <p style={{ marginTop: '1rem', marginBottom: 0 }}>
              Een van onze hypotheekadviseurs neemt binnen 1 werkdag contact met u op
              via uw voorkeurskanaal ({formData.preferredContact === 'email' ? 'e-mail' : 'telefoon'}).
            </p>
          </div>

          <div className="mt-4">
            <button
              className="btn btn-primary"
              onClick={() => {
                setIsSubmitted(false);
                setSubmissionMessage('');
                setErrors({});
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  message: '',
                  preferredContact: 'email',
                  mortgageAmount: undefined,
                  propertyValue: undefined
                });
              }}
            >
              Nieuw bericht versturen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="card">
        <h1>Contact met Hypotheekadviseur</h1>
        <p>
          Heeft u vragen over uw hypotheekmogelijkheden of wilt u persoonlijk advies?
          Vul onderstaand formulier in en een van onze ervaren hypotheekadviseurs
          neemt kosteloos en vrijblijvend contact met u op. Uw contactgegevens worden alleen
          gedeeld met De Heer &amp; Partners.
        </p>
      </div>

      <div className="card">
        <h2>Contactgegevens</h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-2">
            <div className="form-group">
              <label htmlFor="name">
                Naam *
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                  placeholder="Voor- en achternaam"
                />
                {errors.name && <span className="text-error">{errors.name}</span>}
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="email">
                E-mailadres *
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'error' : ''}
                  placeholder="uw.naam@email.nl"
                />
                {errors.email && <span className="text-error">{errors.email}</span>}
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                Telefoonnummer *
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={errors.phone ? 'error' : ''}
                  placeholder="06-12345678"
                />
                {errors.phone && <span className="text-error">{errors.phone}</span>}
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="preferredContact">
                Voorkeur contact
                <select
                  id="preferredContact"
                  value={formData.preferredContact}
                  onChange={(e) => handleInputChange('preferredContact', e.target.value as 'email' | 'phone')}
                >
                  <option value="email">E-mail</option>
                  <option value="phone">Telefoon</option>
                </select>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">
              Uw vraag of bericht *
              <textarea
                id="message"
                rows={6}
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                className={errors.message ? 'error' : ''}
                placeholder="Beschrijf uw situatie en eventuele vragen..."
              />
              {errors.message && <span className="text-error">{errors.message}</span>}
            </label>
          </div>

          <fieldset>
            <legend>Optionele informatie</legend>
            <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-light)' }}>
              Deze informatie helpt ons om u beter te adviseren, maar is niet verplicht.
            </p>

            <div className="grid grid-2">
              <div className="form-group">
                <label htmlFor="mortgageAmount">
                  Gewenst hypotheekbedrag (€)
                  <input
                    type="text"
                    id="mortgageAmount"
                    placeholder="300000"
                    value={formData.mortgageAmount ? formatInputNumber(formData.mortgageAmount.toString()) : ''}
                    onChange={(e) => handleNumberInputChange('mortgageAmount', e)}
                  />
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="propertyValue">
                  Waarde woning (€)
                  <input
                    type="text"
                    id="propertyValue"
                    placeholder="375000"
                    value={formData.propertyValue ? formatInputNumber(formData.propertyValue.toString()) : ''}
                    onChange={(e) => handleNumberInputChange('propertyValue', e)}
                  />
                </label>
              </div>
            </div>
          </fieldset>

          {errors.submit && (
            <div className="alert alert-error">
              {errors.submit}
            </div>
          )}

          <div className="form-group">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Bezig met verzenden...
                </>
              ) : (
                'Verstuur bericht'
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h3>📞 Direct Contact</h3>
          <p><strong>Telefoon:</strong> <a href="tel:+31 23 525 8061">023 525 8061</a></p>
          <p><strong>E-mail:</strong> <a href="mailto:hypotheken@deheer.nl">hypotheken@deheer.nl</a></p>
          <p><strong>Openingstijden:</strong></p>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li>Maandag t/m vrijdag: 09:00 - 17:00</li>
            <li>Zaterdag-Zondag: Gesloten</li>
          </ul>
        </div>

        <div className="card">
          <h3>🏢 Kantoor Bezoek</h3>
          <p><strong>Adres:</strong></p>
          <p>
            de Heer & Partners<br/>
            Hypotheekadviseurs<br/>
            Hagelingerweg 173<br/>
            2071 CD Santpoort-Noord
          </p>
          <p>
            <em>Bezoek op afspraak mogelijk</em>
          </p>
        </div>
      </div>

      <div className="card">
        <h3>Wat kunt u van ons verwachten?</h3>
        <div className="grid grid-3">
          <div>
            <h4>🎯 Persoonlijk Advies</h4>
            <p>Advies op maat, afgestemd op uw specifieke situatie en wensen.</p>
          </div>
          <div>
            <h4>🔍 Onafhankelijk</h4>
            <p>Wij vergelijken alle hypotheekverstrekkers voor de beste condities.</p>
          </div>
          <div>
            <h4>🚀 Snelle Service</h4>
            <p>Reactie binnen 1 werkdag en snelle behandeling van uw aanvraag.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
